// the list of the changes of an automation run (the wizard and the detail of the run) and the row with the buttons
// at the bottom of the card, which is pinned to the bottom of the window when its place is out of view
export default {
    data() {
        return {
            showUnchanged: false,
            footerPinned: false,
            footerStyle: {},
            footerResizeObserver: null
        };
    },

    computed: {
        // the title column names the changed entity (e.g. the employee), the badges and the progress bars describe it
        titleColumn() {
            if (!this.run) {
                return null;
            }

            return this.run.columns.find(column => column.display === 'title') || this.run.columns.find(column => !column.numeric);
        },

        badgeColumns() {
            return this.run ? this.run.columns.filter(column => column !== this.titleColumn && column.display !== 'progress' && column.display !== 'text') : [];
        },

        textColumns() {
            return this.run ? this.run.columns.filter(column => column !== this.titleColumn && column.display === 'text') : [];
        },

        progressColumns() {
            return this.run ? this.run.columns.filter(column => column !== this.titleColumn && column.display === 'progress') : [];
        },

        sortedItems() {
            if (!this.run) {
                return [];
            }

            return this.run.items.slice().sort((a, b) => String(this.title(a)).localeCompare(String(this.title(b))));
        },

        writtenItems() {
            return this.sortedItems.filter(item => item.status_code === 'success');
        },

        failedItems() {
            return this.sortedItems.filter(item => item.status_code === 'failed');
        },

        unchangedItems() {
            return this.sortedItems.filter(item => item.status_code === 'unchanged');
        }
    },

    mounted() {
        window.addEventListener('resize', this.updateFooterPosition);
        document.addEventListener('scroll', this.updateFooterPosition, true);
        // the height of the card changes after it is rendered (e.g. the fonts), the footer has to follow it
        if (window.ResizeObserver) {
            this.footerResizeObserver = new ResizeObserver(this.updateFooterPosition);
            this.footerResizeObserver.observe(this.$el);
        }
        this.updateFooterPosition();
    },

    updated() {
        this.updateFooterPosition();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.updateFooterPosition);
        document.removeEventListener('scroll', this.updateFooterPosition, true);
        if (this.footerResizeObserver) {
            this.footerResizeObserver.disconnect();
        }
    },

    methods: {
        // the preview columns are known only in the wizard (they are not stored), otherwise the label of the item names it
        title(item) {
            return this.titleColumn && item.columns ? item.columns[this.titleColumn.key] : this.labelParts(item)[0];
        },

        // the rest of the label describes the change like the badges of the preview (e.g. the contract type and the month)
        labelBadges(item) {
            return item.columns ? [] : this.labelParts(item).slice(1);
        },

        // the target joins the parts of the label with " · ", the first part names the changed entity
        labelParts(item) {
            return String(item.label || '').split(' · ');
        },

        // the avatar of a change shows the initials of its title (e.g. "Bc. Vladimír" -> "BV")
        initials(item) {
            return String(this.title(item)).split(/\s+/).filter(word => word).slice(0, 2).map(word => word.charAt(0).toUpperCase()).join('');
        },

        // the width of a progress bar in percent, null for a value which is not a number (e.g. "-")
        progress(value) {
            const number = parseFloat(String(value).replace(',', '.'));
            return isNaN(number) ? null : Math.max(0, Math.min(100, number));
        },

        // the row with the buttons stays in the card; when its place is below the bottom of the window (many rows),
        // it is pinned to the bottom of the window so it stays visible (the same as in the settings of the contract type)
        updateFooterPosition() {
            const holder = this.$refs.footerHolder;
            if (!holder) {
                if (this.footerPinned) {
                    this.footerPinned = false;
                    this.footerStyle = {};
                }
                return;
            }

            const rect = holder.getBoundingClientRect();
            const pinned = rect.bottom > window.innerHeight;
            const style = pinned ? { left: rect.left + 'px', width: rect.width + 'px' } : {};
            // updated() calls this again, so only a real change may touch the data, otherwise it loops
            // (an unpinned footer has no style, so undefined has to be compared with undefined, not with null)
            if (pinned === this.footerPinned && style.left === this.footerStyle.left && style.width === this.footerStyle.width) {
                return;
            }

            this.footerPinned = pinned;
            this.footerStyle = style;
        }
    }
};



// WEBPACK FOOTER //
// ./resources/assets/admin/js/components/AutomationRunItems.js